import { differenceInMonths, parseISO } from 'date-fns';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../types';
export function calculatePromotionStats(
  user: UserProfile,
  events: ClubEvent[],
  attendance: Attendance[]
) {
  const userAttendance = attendance.filter(a => a.userId === user.uid && a.status === 'attending');
  const attendedEvents = events.filter(e => userAttendance.some(a => a.eventId === e.id));
  
  const totalPoints = attendedEvents.reduce((sum, e) => sum + e.points, 0) + (user.manualPoints || 0);
  const eventCount = attendedEvents.length;
  const meetingCount = attendedEvents.filter(e => e.type === 'Meeting').length;
  
  const joinDate = user.zavalaJoinDate ? parseISO(user.zavalaJoinDate) : new Date();
  const rankDate = user.promotionRankDate ? parseISO(user.promotionRankDate) : new Date();
  
  const monthsAsMember = (!isNaN(joinDate.getTime())) ? differenceInMonths(new Date(), joinDate) : 0;
  const monthsSinceLastPromotion = (!isNaN(rankDate.getTime())) ? differenceInMonths(new Date(), rankDate) : 0;
  
  return {
    totalPoints,
    eventCount,
    meetingCount,
    monthsAsMember,
    monthsSinceLastPromotion,
    attendedEvents
  };
}

export function getEligibleRank(
  user: UserProfile,
  stats: ReturnType<typeof calculatePromotionStats>,
  ranks: RankRequirement[]
): RankRequirement {
  if (!ranks || ranks.length === 0) {
    return { id: 'CRMN', name: 'Crewman', shortName: 'Crew', order: 10 };
  }
  
  const currentRankIndex = ranks.findIndex(r => r.id === user.currentRank || (user.currentRank === 'CDR' && r.id === 'CMDR'));
  const currentRankObj = currentRankIndex !== -1 ? ranks[currentRankIndex] : ranks[0];

  // Restrict promotion for Community rank
  if (user.currentRank === 'COMM') {
    return currentRankObj;
  }

  // Restrict promotion for guests/volunteers
  if (user.roles?.includes('guest') || user.isGuest) {
    return currentRankObj;
  }

  // Restrict promotion for non-Zavala crew
  if (!user.shipName || !user.shipName.toLowerCase().includes('zavala')) {
    return currentRankObj;
  }
  
  let eligibleRank = ranks[0]; // Default to first rank

  for (const rank of ranks) {
    let isEligible = true;

    // Tenure check
    if (rank.minMonths && stats.monthsAsMember < rank.minMonths) isEligible = false;

    // Event check
    if (rank.minEvents && stats.eventCount < rank.minEvents) isEligible = false;

    // Points check
    if (rank.minPoints && stats.totalPoints < rank.minPoints) isEligible = false;

    // OTS/OCC check
    if (rank.otsRequired && !user.ots) isEligible = false;
    if (rank.occRequired && !user.occ) isEligible = false;

    // Previous rank tenure check
    if (rank.previousRankId && rank.previousRankMonths) {
      const isCorrectPreviousRank = user.currentRank === rank.previousRankId;
      const hasEnoughTenureInRank = stats.monthsSinceLastPromotion >= rank.previousRankMonths;
      
      if (!isCorrectPreviousRank || !hasEnoughTenureInRank) {
        // Special case for LTJG: OR OTS check and OCC checked
        if (rank.id === 'LTJG' && user.ots && user.occ) {
          // If they have OTS/OCC, they bypass the tenure/previous rank check for LTJG
          // But they still need to satisfy points/events/etc. (which are checked above)
        } else {
          isEligible = false;
        }
      }
    }

    if (isEligible) {
      eligibleRank = rank;
    }
  }

  return eligibleRank;
}
